/**
 * Guide for Unternehmensprofil › Leistungen.
 *
 * Written for salon owners: what the screen is for, what to click, and what a
 * customer notices. The question-mark icon on the card links here.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Scissors } from 'lucide-react';

const STEPS: { title: string; body: React.ReactNode }[] = [
  {
    title: 'Salon wählen',
    body: (
      <>
        Im{' '}
        <Link to="/profile?tab=leistungen" className="text-[#1967D2] hover:underline">
          Unternehmensprofil → Leistungen
        </Link>{' '}
        oben den Salon auswählen. Jeder Salon hat seine eigene Zuteilung, weil jeder Salon
        seine eigenen Gruppen in TimeGlobe hat.
      </>
    ),
  },
  {
    title: 'Gruppe zuteilen',
    body: (
      <>
        Neben jeder Gruppe stehen <strong>Damen</strong> und <strong>Herren</strong>.{' '}
        <strong>Blau heißt: Die Gruppe steht in der Liste für diese Kunden.</strong> Beides
        blau bedeutet für alle. Ist nichts blau, steht die Gruppe nur in der Gesamtliste.
      </>
    ),
  },
  {
    title: 'Vorschau ansehen',
    body: (
      <>
        Rechts siehst du die Nachricht, die eine Kundin, ein Kunde oder jemand ohne Angabe
        bekommt. Jeder Klick wird sofort gespeichert und gilt spätestens nach einer Minute.
      </>
    ),
  },
];

const FACTS: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'Kann ein Kunde trotzdem alles buchen?',
    answer: (
      <>
        Ja. Die Zuteilung ordnet nur die Übersicht. Nennt ein Kunde eine Leistung, findet der
        Agent sie – egal in welcher Gruppe sie steht.
      </>
    ),
  },
  {
    question: 'Woher kennt der Agent das Geschlecht?',
    answer: (
      <>
        Aus der Anrede im Kundenprofil von TimeGlobe. Fehlt sie, bekommt der Kunde die
        Gesamtliste mit allen Gruppen.
      </>
    ),
  },
  {
    question: 'Warum fehlen Zusatzleistungen in der Übersicht?',
    answer: (
      <>
        Weil sie nur zusammen mit einer Hauptleistung buchbar sind. Der Agent bietet sie
        passend zur gewählten Leistung an; welche er anbieten darf, stellst du beim{' '}
        <Link to="/voice-agent" className="text-[#1967D2] hover:underline">
          Agenten unter Up- &amp; Cross-Selling
        </Link>{' '}
        ein.
      </>
    ),
  },
  {
    question: 'Was passiert bei sehr vielen Leistungen?',
    answer: (
      <>
        Passt die Liste nicht in eine WhatsApp-Nachricht, nennt der Agent die Bereiche (eure
        Gruppennamen) und fragt nach dem Wunsch.
      </>
    ),
  },
  {
    question: 'Gilt das auch am Telefon?',
    answer: <>Nein. Die Übersicht gibt es in WhatsApp; am Telefon fragt der Agent nach dem Wunsch.</>,
  },
];

const ServiceGroupsGuide: React.FC = () => (
  <div className="space-y-6">
    <Card className="shadow-sm border-border">
      <CardHeader>
        <div className="flex items-start gap-3">
          <Scissors className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
          <div>
            <CardTitle className="text-lg">Leistungen: Wem zeigt der Agent was?</CardTitle>
            <CardDescription>
              Tippt ein Kunde in WhatsApp auf „Termin buchen“, bekommt er eine Übersicht eurer
              Leistungen – Damen die Gruppen für Damen, Herren die für Herren.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">Brauche ich das überhaupt?</p>
          <p className="text-sm text-muted-foreground">
            Meistens nicht. Beginnt der Name einer Gruppe in TimeGlobe mit{' '}
            <strong>Damen</strong> oder <strong>Herren</strong> („Damen Color“, „Herren
            Pakete“), erkennt der Agent das von selbst. Du greifst nur ein, wenn eine Gruppe
            anders heißt, als sie gemeint ist – etwa „Bart &amp; Rasur“ für Herren oder
            „Hochsteckfrisuren“, die nur Damen sehen sollen.
          </p>
        </div>

        <ol className="space-y-5">
          {STEPS.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span className="shrink-0 h-8 w-8 rounded-full bg-[#1967D2] text-white text-sm font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <div className="space-y-1 pt-1">
                <p className="font-medium text-foreground">{step.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>

    <Card className="shadow-sm border-border">
      <CardHeader>
        <CardTitle className="text-lg">Gut zu wissen</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-4">
          {FACTS.map((fact) => (
            <div key={fact.question} className="space-y-1">
              <dt className="text-sm font-medium text-foreground">{fact.question}</dt>
              <dd className="text-sm text-muted-foreground leading-relaxed">{fact.answer}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  </div>
);

export default ServiceGroupsGuide;
